/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Users, Gift, Home, Coins, Scale, ScrollText, Calendar, Calculator,
} from 'lucide-react';
import GuidePage from '@/components/library/GuidePage';

const config = {
  path: '/donation-partage',
  slug: 'donation-partage',
  breadcrumbLabel: 'Donation-partage',
  readingTime: '7 min',
  lastUpdated: '18 février 2026',
  seoTitle: 'Donation-partage : explications simples | HÉRITA',
  seoDescription:
    "Comprenez facilement une donation-partage : son rôle, ce qu'elle contient, les conséquences pour chaque bénéficiaire. Analysez votre acte avec HÉRITA.",

  hero: {
    eyebrow: 'Guides HÉRITA',
    title: 'Comprendre une donation-partage',
    subtitle:
      "La donation-partage permet de transmettre et de partager son patrimoine de son vivant. HÉRITA vous aide à comprendre qui reçoit quoi, sous quelles conditions et avec quelles conséquences pour la future succession.",
  },

  definition: {
    eyebrow: 'Définition',
    title: "Qu'est-ce qu'une donation-partage\u00A0?",
    paragraphs: [
      <>
        Une donation-partage est un acte authentique reçu par un notaire qui
        permet à une ou deux personnes (souvent les parents) de
        <strong className="font-semibold text-[#1A2530]"> transmettre ET de répartir, de leur vivant</strong>,
        tout ou partie de leur patrimoine entre leurs descendants.
        Contrairement à une donation classique, elle ne se contente pas de
        donner&nbsp;: elle fige, dès maintenant, qui recevra quoi.
      </>,
      <>
        L'intérêt principal est d'<em>éviter les conflits futurs</em>. Les
        biens donnés sont évalués au jour de l'acte, et cette évaluation
        s'impose au règlement de la succession à venir. Plus tard, au décès du
        donateur, ces valeurs ne seront pas réévaluées&nbsp;: chacun est censé
        avoir déjà reçu sa part, et le partage est en grande partie réglé.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Qui peut en bénéficier&nbsp;?</strong>{' '}
        Les enfants en premier lieu, mais aussi les petits-enfants (avec
        l'accord des parents). Lorsque deux époux donnent ensemble, on parle
        de donation-partage conjonctive&nbsp;: les biens communs sont
        attribués indifféremment aux enfants des deux époux.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Avec ou sans réserve d'usufruit.</strong>{' '}
        Beaucoup de donations-partages sont faites en démembrement&nbsp;: les
        parents donnent la <em>nue-propriété</em> et conservent
        l'<em>usufruit</em>. Concrètement, ils gardent l'usage du bien et ses
        revenus jusqu'à leur décès. À leur disparition, les bénéficiaires
        deviennent pleinement propriétaires sans droit supplémentaire à payer.
      </>,
      <>
        L'acte de donation-partage est dense&nbsp;: il décrit chaque bien
        attribué, sa valeur retenue, à qui il revient, les éventuelles
        <em> soultes</em> (sommes d'argent versées pour équilibrer les lots),
        les charges et les conditions. Il rappelle aussi les donations
        antérieures pour que tout reste équitable.
      </>,
      <>
        C'est précisément cette densité que HÉRITA vous aide à digérer&nbsp;:
        identifier rapidement votre lot, sa valeur, vos obligations
        éventuelles, et comprendre l'effet sur la future{' '}
        <Link
          to="/declaration-succession"
          className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]"
        >déclaration de succession</Link> de vos parents.
      </>,
    ],
  },

  usage: {
    eyebrow: 'Utilité',
    title: "À quoi sert-elle\u00A0?",
    items: [
      { title: 'Transmettre de son vivant', description: "Donner permet d'organiser la transmission avant le décès, en bénéficiant d'abattements fiscaux qui se renouvellent tous les 15 ans." },
      { title: 'Figer les valeurs', description: "Les biens sont évalués au jour de la donation. Cette valeur ne sera plus contestable au moment de la succession." },
      { title: 'Éviter les conflits', description: "Chaque enfant connaît à l'avance son lot. C'est l'outil le plus efficace pour prévenir les contestations futures." },
      { title: 'Optimiser la fiscalité', description: "Avec démembrement, seule la nue-propriété est taxée. Les droits à payer sont nettement réduits." },
    ],
  },

  content: {
    eyebrow: 'Contenu',
    title: "Que contient une donation-partage\u00A0?",
    intro: "Huit éléments structurent l'acte. Leur articulation détermine ce que chacun reçoit, à quel prix et avec quelles obligations.",
    cards: [
      { Icon: Users, title: 'Donateurs', description: "Les parents (ou grands-parents) qui donnent, avec leur état civil, leur régime matrimonial et leur capacité juridique." },
      { Icon: Users, title: 'Bénéficiaires', description: "Les descendants qui reçoivent. Chacun est nommé avec son lot précis et la quote-part attribuée." },
      { Icon: Home, title: 'Biens donnés', description: "Maison, appartement, parts de SCI, portefeuille titres, espèces : chaque bien est identifié et localisé." },
      { Icon: Coins, title: 'Valeur retenue', description: "Le montant exact attribué à chaque bien au jour de l'acte. C'est ce chiffre qui fera foi en succession." },
      { Icon: Gift, title: 'Démembrement éventuel', description: "Donation de la pleine propriété ou seulement de la nue-propriété, avec réserve d'usufruit pour les donateurs." },
      { Icon: Scale, title: 'Soultes', description: "Sommes versées d'un bénéficiaire à un autre pour équilibrer les lots quand un bien ne peut être partagé." },
      { Icon: ScrollText, title: 'Charges et conditions', description: "Obligations imposées aux bénéficiaires : interdiction d'aliéner, droit de retour, clauses d'inaliénabilité…" },
      { Icon: Calculator, title: 'Droits de donation', description: "Le détail du calcul fiscal après application de l'abattement personnel (100 000 € par enfant et par parent)." },
    ],
  },

  checks: {
    eyebrow: 'Vigilance',
    title: 'Les points importants à vérifier',
    intro: "Une donation-partage est un acte qui engage durablement. Voici ce qu'il faut relire avec attention avant de signer.",
    items: [
      "S'assurer que tous les enfants — y compris d'unions précédentes — sont concernés par l'acte.",
      "Vérifier que la valeur retenue pour chaque bien correspond à sa valeur de marché actuelle.",
      "Comprendre précisément ce qui est donné : pleine propriété ou simple nue-propriété.",
      "Identifier les éventuelles soultes à verser et leurs modalités de paiement.",
      "Relire les charges imposées : interdiction de vendre, droit de retour, obligation d'entretien.",
      "Confirmer le rappel des donations antérieures pour préserver l'équilibre entre héritiers.",
    ],
  },

  autoChecks: {
    eyebrow: 'Sans rien demander',
    title: 'Ce que HÉRITA identifie automatiquement',
    intro: "Dès l'import de l'acte, HÉRITA repère ce qui vous concerne et le reformule en français clair.",
    items: [
      'Donateurs et bénéficiaires identifiés',
      "Biens donnés et leur localisation",
      'Valeurs retenues au jour de la donation',
      'Démembrement éventuel (nue-propriété, usufruit)',
      'Soultes à verser ou à recevoir',
      'Charges et conditions à respecter',
      "Droits de donation à payer",
      'Questions à poser à votre notaire',
    ],
  },

  example: {
    eyebrow: 'Avant · Après',
    title: 'Un exemple simplifié',
    intro: "À gauche, un extrait fictif d'acte de donation-partage. À droite, ce qu'HÉRITA en aurait expliqué.",
    extract: (
      <>
        «&nbsp;Monsieur et Madame DUBOIS, mariés sous le régime de la
        communauté universelle, attribuent à leur fils unique Antoine DUBOIS
        la nue-propriété de la maison sise [Adresse], évaluée à la somme de
        QUATRE CENT VINGT MILLE EUROS (420 000 €), à charge pour lui de
        respecter l'usufruit que les donateurs se réservent leur vie durant,
        et à condition de ne pas aliéner ledit bien avant leur décès,
        conformément aux dispositions des articles 1075 et suivants du Code
        civil.&nbsp;»
      </>
    ),
    headline: "Ce que cela signifie pour Antoine",
    bullets: [
      { strong: 'Vous recevez la nue-propriété', rest: " d'une maison évaluée à 420 000 €." },
      { strong: 'Vos parents conservent l\'usufruit', rest: ' : ils peuvent y habiter ou la louer jusqu\'à leur décès.' },
      { strong: 'Vous ne pouvez pas vendre le bien', rest: ' tant que vos parents sont en vie : c\'est une clause d\'inaliénabilité.' },
      { strong: 'À leur décès', rest: ', vous deviendrez plein propriétaire automatiquement, sans nouveau droit à payer.' },
      { strong: 'La valeur retenue (420 000 €)', rest: ' s\'imposera lors de la future succession, même si la maison vaut plus à ce moment-là.' },
    ],
  },

  faq: [
    { q: "Qu'est-ce qu'une donation-partage ?", a: "C'est un acte authentique notarié par lequel une ou deux personnes donnent ET partagent, de leur vivant, tout ou partie de leur patrimoine entre leurs descendants. Elle fige la valeur des biens et la répartition pour anticiper la succession." },
    { q: "Quelle différence avec une donation simple ?", a: "Une donation simple donne un bien sans le partager. La donation-partage donne ET répartit, ce qui empêche tout litige sur la valeur des biens au moment du décès. C'est l'outil le plus protecteur des familles recomposées ou nombreuses." },
    { q: "Faut-il l'accord de tous les enfants ?", a: "Tous les enfants doivent être appelés à l'acte. Un enfant peut renoncer à recevoir un lot, mais il ne peut être exclu sans son accord. Inclure les enfants d'unions précédentes est essentiel pour la validité de l'acte." },
    { q: "Quels sont les droits à payer ?", a: "Les droits de donation s'appliquent après un abattement de 100 000 € par enfant et par parent, renouvelable tous les 15 ans. Au-delà, le barème progressif (de 5 % à 45 %) s'applique. En cas de démembrement, seule la nue-propriété est taxée." },
    { q: "Peut-on annuler une donation-partage ?", a: "Très difficilement. La donation-partage est en principe irrévocable. Elle ne peut être révoquée que dans trois cas exceptionnels : inexécution des charges, ingratitude grave du bénéficiaire, ou survenance d'un enfant après l'acte." },
    { q: "Comment HÉRITA l'analyse-t-il ?", a: "HÉRITA lit l'acte intégralement, identifie chaque bénéficiaire, son lot, la valeur retenue, le démembrement, les soultes et les charges. Vous obtenez une lecture claire, structurée et personnalisée en quelques minutes." },
    { q: "Que se passe-t-il au décès du donateur ?", a: "Si la donation-partage couvre tout le patrimoine, le partage est déjà réglé : peu de choses restent à faire. Les biens donnés ne sont pas réévalués, et la succession se limite aux biens non donnés et aux acquisitions postérieures à l'acte." },
    { q: "Peut-on donner uniquement la nue-propriété ?", a: "Oui, c'est même la formule la plus courante. Les parents conservent l'usufruit (l'usage et les revenus du bien) jusqu'à leur décès. À ce moment, l'usufruit s'éteint et l'enfant devient plein propriétaire sans droit supplémentaire." },
    { q: "Une soulte est-elle obligatoire ?", a: "Pas toujours. Une soulte est versée uniquement lorsqu'un bien ne peut pas être divisé équitablement entre plusieurs enfants. Celui qui reçoit le bien indemnise alors ses frères et sœurs en numéraire ou sur d'autres biens." },
    { q: "Quel est le rapport avec la succession future ?", a: "La donation-partage anticipe la succession. Au décès du donateur, la valeur des biens donnés ne sera pas réévaluée. Le partage est en grande partie déjà figé, ce qui simplifie énormément le règlement et limite les conflits." },
  ],

  finalCta: {
    title: "Vous avez reçu une donation-partage\u00A0?",
    subtitle: "Déposez votre acte. HÉRITA identifie automatiquement les biens donnés, votre lot précis, les valeurs retenues, le démembrement éventuel et les charges associées afin de vous fournir une explication claire et personnalisée.",
  },
};

const DonationPartage = () => <GuidePage config={config} />;
export default DonationPartage;
